import React, { useState, useEffect, useRef } from "react";
import { RotaDropdownHeader, RotaMultiSelectMenu } from "@teamrota/rota-design";
import { uniqBy } from "lodash";

import useAuth from "~/src/auth/hooks/use-auth";

import { MEMBER_INFO, MEMBER_TYPES } from "~/src/consts";

import {
  DEFAULT_STATES,
  getSelectedIds,
  filterBySearchTerm,
  clearFilteredOptions,
  formatUsers,
  capitalize,
  isLastItem
} from "./utils";

import {
  StyledRotaDropdownHeader,
  StyledDropdownsContainer,
  StyledSeparator,
  StyledFilterWrapper,
  StyledChipsContainer
} from "./filter-dropdown.styles";
import FilterChips from "./FilterChips/filter-chips";
import {
  getProvideScheduleFilters,
  setProvideScheduleFilters
} from "../../storage";
import { useUsers } from "~/src/containers/provide-schedule/use-users";

const FilterDropdown = ({
  partners,
  handlePartnerSelect,
  setRoleIds,
  setVenueIds,
  isShowCancelledShifts,
  setIsShowCancelledShifts,
  setSelectedMemberTypes,
  setSelectedMemberInfo,
  setSelectedUserIds
}) => {
  // ================= STATE ==================
  const [filters, setFilters] = useState(DEFAULT_STATES.FILTERS);
  const [openDropdowns, setOpenDropdowns] = useState(DEFAULT_STATES.DROPDOWNS);

  const chipsContainerRef = useRef();

  const { users, isUsersLoading } = useUsers();

  const auth = useAuth();
  const currentUserId = auth.info.id;

  // ================= COMPUTED_STATE ==================
  const selectedPartnersIds = getSelectedIds("partnerFilter", filters);
  const selectedRolesIds = getSelectedIds("roleFilter", filters);
  const selectedVenuesIds = getSelectedIds("venueFilter", filters);
  const selectedMemberTypesIds = getSelectedIds("memberTypeFilter", filters);
  const selectedMemberInfoIds = getSelectedIds("memberInfoFilter", filters);
  const selectedShiftStatusIds = getSelectedIds("shiftStatusFilter", filters);
  const selectedUsersIds = getSelectedIds("createdByFilter", filters);

  const filtersArray = Object.values(filters);

  const selectedPartners = selectedPartnersIds.length
    ? partners.filter(partner => selectedPartnersIds.includes(partner.id))
    : partners;

  const rolesOfSelectedPartners = uniqBy(
    selectedPartners.map(partner => partner.roleRates).flat(),
    "roleId"
  );

  const venuesOfSelectedPartners = selectedPartners
    .map(partner => partner.venues)
    .flat();

  const isAnyFilterSelected = filtersArray.some(
    filter => filter.selectedItems.length
  );

  // ================= STATE HANDLERS ==================
  // ---- filters ----
  const handleSelect = (selectedItem, filterName) => {
    if (getSelectedIds(filterName, filters).includes(selectedItem.value)) {
      setFilters({
        ...filters,
        [filterName]: {
          ...filters[filterName],
          selectedItems: filters[filterName].selectedItems.filter(
            item => item.value !== selectedItem.value
          )
        }
      });
    } else {
      setFilters({
        ...filters,
        [filterName]: {
          ...filters[filterName],
          selectedItems: [...filters[filterName].selectedItems, selectedItem]
        }
      });
    }
  };

  const handleSearch = (value, filterName) => {
    setFilters({
      ...filters,
      [filterName]: {
        ...filters[filterName],
        searchTerm: value
      }
    });
  };

  const handleClear = filterName => {
    setFilters({
      ...filters,
      [filterName]: {
        ...filters[filterName],
        selectedItems: []
      }
    });
  };

  // ---- dropdown ----
  const toggleDropdown = name => {
    if (openDropdowns.main === true && name === "main") {
      setOpenDropdowns(DEFAULT_STATES.DROPDOWNS);
    } else {
      setOpenDropdowns({ ...openDropdowns, [name]: !openDropdowns[name] });
    }
  };

  const setDropdownOpen = name => {
    setOpenDropdowns({
      ...DEFAULT_STATES.DROPDOWNS,
      main: true,
      [name]: true
    });
  };

  // ================= DROPDOWN OPTIONS ==================
  const dropdownOptions = {
    partnerFilter: partners
      ?.map(partner => ({
        value: partner.id,
        label: partner.accountName,
        selected: selectedPartnersIds.includes(partner.id)
      }))
      .sort((a, b) =>
        a.label.toLowerCase().localeCompare(b.label.toLowerCase())
      ),

    roleFilter: rolesOfSelectedPartners
      ?.map(role => ({
        value: role.roleId,
        label: role.roleName,
        selected: selectedRolesIds.includes(role.roleId)
      }))
      .sort((a, b) =>
        a.label.toLowerCase().localeCompare(b.label.toLowerCase())
      ),

    venueFilter: venuesOfSelectedPartners
      ?.map(venue => ({
        value: venue.id,
        label: venue.name,
        selected: selectedVenuesIds.includes(venue.id)
      }))
      .sort((a, b) =>
        a.label.toLowerCase().localeCompare(b.label.toLowerCase())
      ),

    memberTypeFilter: MEMBER_TYPES.map(({ value, label }) => ({
      value,
      label: capitalize(label),
      selected: selectedMemberTypesIds.includes(value)
    })),

    memberInfoFilter: MEMBER_INFO.map(({ value, label }) => ({
      value,
      label: capitalize(label),
      selected: selectedMemberInfoIds.includes(value)
    })),

    shiftStatusFilter: [
      {
        value: "CANCELLED_SHIFTS",
        label: "Show cancelled shifts",
        selected: !!isShowCancelledShifts
      }
    ],

    createdByFilter: isUsersLoading
      ? []
      : formatUsers(users, currentUserId).map(user => ({
          ...user,
          selected: selectedUsersIds.includes(user.value)
        }))
  };

  // ================= USE EFFECTS =================

  useEffect(() => {
    handlePartnerSelect(selectedPartnersIds);
    clearFilteredOptions(filters, setFilters, dropdownOptions);
  }, [selectedPartnersIds.length]);

  useEffect(() => {
    setRoleIds(selectedRolesIds);
  }, [selectedRolesIds.length]);

  useEffect(() => {
    setVenueIds(selectedVenuesIds);
  }, [selectedVenuesIds.length]);

  useEffect(() => {
    setSelectedMemberTypes(selectedMemberTypesIds);
  }, [selectedMemberTypesIds.length]);

  useEffect(() => {
    setSelectedMemberInfo(selectedMemberInfoIds);
  }, [selectedMemberInfoIds.length]);

  useEffect(() => {
    setSelectedUserIds(selectedUsersIds);
  }, [selectedUsersIds.length]);

  useEffect(() => {
    setIsShowCancelledShifts(
      !!selectedShiftStatusIds.includes("CANCELLED_SHIFTS")
    );
  }, [selectedShiftStatusIds.length]);

  useEffect(() => {
    const preselectedFilters = getProvideScheduleFilters({
      id: currentUserId
    });
    if (preselectedFilters) {
      setFilters({
        ...DEFAULT_STATES.FILTERS,
        ...preselectedFilters
      });
    }
  }, []);

  useEffect(() => {
    const filtersToSave = { ...filters };
    // Do not save searchTerm into local storage
    Object.keys(filtersToSave).forEach(
      filterKey =>
        (filtersToSave[filterKey] = {
          ...filtersToSave[filterKey],
          searchTerm: ""
        })
    );
    setProvideScheduleFilters({
      id: currentUserId,
      filters: filtersToSave
    });
  }, [filters]);

  // ================= RENDER =================
  return (
    <StyledFilterWrapper>
      <RotaDropdownHeader
        labelText={"Filter by..."}
        isOpen={openDropdowns.main}
        setIsOpen={() => toggleDropdown("main")}
        shouldShowDot={isAnyFilterSelected}
      >
        <StyledDropdownsContainer>
          {filtersArray.map(
            ({ name, displayName, searchPlaceholder }, index) => (
              <div key={name}>
                <StyledRotaDropdownHeader
                  labelText={displayName.toUpperCase()}
                  isOpen={openDropdowns[name]}
                  setIsOpen={() => toggleDropdown(name)}
                  shouldShowDot={!!filters[name].selectedItems.length}
                  opensToRight
                  onClose={() => handleSearch("", name)}
                  searchPlaceholder={searchPlaceholder}
                >
                  <RotaMultiSelectMenu
                    items={filterBySearchTerm(
                      dropdownOptions[name],
                      filters[name].searchTerm
                    )}
                    onSearch={value => handleSearch(value, name)}
                    onSelect={item => handleSelect(item, name)}
                    onClear={() => handleClear(name)}
                    showSearchInput={name !== "shiftStatusFilter"}
                  />
                </StyledRotaDropdownHeader>
                {!isLastItem(filtersArray, index) && <StyledSeparator />}
              </div>
            )
          )}
        </StyledDropdownsContainer>
      </RotaDropdownHeader>
      <StyledChipsContainer ref={chipsContainerRef}>
        <FilterChips
          filters={filters}
          handleClear={handleClear}
          setDropdownOpen={setDropdownOpen}
          containerWidth={chipsContainerRef?.current?.clientWidth}
        />
      </StyledChipsContainer>
    </StyledFilterWrapper>
  );
};

export default FilterDropdown;
